import {
    NextLocale,
    PREFERENCES,
    PreferencesData,
} from '@beat-venues/common-types';
import {
    Button,
    CheckboxGroup,
    CheckboxSlider,
    Container,
    Form,
    Heading,
    Paragraph,
    Text,
} from '@beat-venues/ui/react';
import { useMutation } from '@tanstack/react-query';
import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { getRecaptchaToken } from '../../utils';

interface WhatsappProps {
    userId: string;
    email: string;
    userPreferences?: PreferencesData[];
}

export const Whatsapp = ({
    userId,
    email,
    userPreferences,
}: WhatsappProps): ReactElement => {
    const { t } = useTranslation('');
    const [whatsapp, setWhatsapp] = useState<boolean>(
        userPreferences?.includes('MOB_WhatsApp') || false
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [searchParams] = useSearchParams();
    const userLanguage = searchParams.get('language') as NextLocale;

    const mutation = useMutation({
        mutationFn: async ({
            preferences,
            recaptchaToken,
        }: {
            preferences: PreferencesData[];
            recaptchaToken: string;
        }) => {
            const response = await fetch(
                `/api/preferences/${whatsapp ? 'optIn' : 'optOut'}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: email,
                        preferences: preferences,
                        language: userLanguage,
                        recaptchaToken,
                        id: userId,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(t('error.preferences') || '');
            }
            setSuccessMessage(t('messages.successUpdate'));

            return response.json();
        },
        onError: (error) => {
            setErrorMessage(error.message);
        },
    });

    const handleSubmit = async (e: FormEvent) => {
        setErrorMessage(null);
        setSuccessMessage(null);
        e.preventDefault();
        mutation.mutate({
            preferences: [PREFERENCES['whatsapp']],
            recaptchaToken: await getRecaptchaToken(),
        });
    };

    return (
        <Container className="mb-40" maxWidth="lg">
            <Heading as="h1" className="xl:ml-0">
                {t('preferences.whatsapp.title')}
            </Heading>
            <Heading as="h3" className="mb-4">
                {t('preferences.whatsapp.description')}
            </Heading>
            {errorMessage && (
                <Paragraph className="bg-danger-500 my-4 rounded-sm p-2 pl-4">
                    {errorMessage}
                </Paragraph>
            )}
            {successMessage && (
                <Paragraph className="bg-success-500 my-4 rounded-sm p-2 pl-5">
                    &#10003; {successMessage}
                </Paragraph>
            )}
            <Form onSubmit={handleSubmit}>
                <Text className="mb-4">
                    {t('preferences.whatsapp.subscriptionTitle')}
                </Text>
                <CheckboxGroup className="mb-12 lg:w-1/2">
                    {[
                        <div className="mb-4">
                            <CheckboxSlider
                                checked={whatsapp}
                                label={t('preferences.whatsapp.label')}
                                venueId="beat"
                                onChange={(e) => {
                                    setWhatsapp(e.target.checked);
                                }}
                            />
                        </div>,
                    ]}
                </CheckboxGroup>

                <Button
                    disabled={mutation.isPending}
                    intent="primary"
                    type="submit"
                >
                    {t('userAccount.submit')}
                    {/* {userPreferences?.includes('MOB_WhatsApp')
                        ? t('preferences.whatsapp.submit')
                        : t('userAccount.submit')} */}
                </Button>
            </Form>
        </Container>
    );
};
